import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, TrendingUp, LogIn, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function formatError(detail) {
  if (!detail) return "Anmeldung fehlgeschlagen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Bitte E-Mail und Passwort eingeben.");
      return;
    }
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Willkommen zurück im Hammerl-Netzwerk.");
      const next = location.state?.from || "/play";
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || "Anmeldung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-hammerl-paper text-hammerl-ink">
      <header className="border-b-2 border-hammerl-ink bg-hammerl-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/brand/hammerl-logo.jpg" alt="Hobby Horsing Hammerl Logo" className="w-10 h-10 object-cover border border-hammerl-ink" />
            <div>
              <div className="font-bold text-sm tracking-tight">HOBBY HORSING HAMMERL GMBH &amp; CO. KG</div>
              <div className="text-xs text-hammerl-pencil font-mono">Kompetenzzentrum für pferdegestützte Fortbewegung ohne Pferd</div>
            </div>
          </Link>
          <button
            onClick={() => navigate("/scoreboard")}
            className="hidden md:flex items-center gap-1 text-xs font-mono uppercase hover:bg-hammerl-highlight hover:text-hammerl-paper px-2 py-1 transition-colors text-hammerl-ink"
            data-testid="login-scoreboard-link"
          >
            <BarChart3 className="w-3 h-3" /> Hammerl Charts
          </button>
        </div>
      </header>

      <main className="max-w-md mx-auto px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-xs font-mono uppercase tracking-widest text-hammerl-pencil mb-2">Reiter-Portal // Anmeldung</div>
          <h1 className="font-bold text-4xl sm:text-5xl tracking-tighter leading-tight">
            <span className="bg-hammerl-highlight px-2 -rotate-1 inline-block text-hammerl-paper">Anmelden</span>
          </h1>
          <p className="mt-3 text-sm text-hammerl-pencil">
            Setze deine Galopp-Karriere fort. Deine Reiter-Akte wurde sicher in der Hammerl-Cloud archiviert.
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} className="mt-8 bg-hammerl-card border-2 border-hammerl-ink shadow-brutal p-6 space-y-4" data-testid="login-form">
          <div>
            <label className="font-bold text-sm">E-Mail</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="reiter@galopp.de"
              autoComplete="email"
              className="mt-1 rounded-none border-0 border-b-2 border-hammerl-ink bg-transparent focus-visible:ring-0 focus-visible:border-hammerl-corpblue px-1"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <label className="font-bold text-sm">Passwort</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="••••••••"
              className="mt-1 rounded-none border-0 border-b-2 border-hammerl-ink bg-transparent focus-visible:ring-0 focus-visible:border-hammerl-corpblue px-1"
              data-testid="login-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full rounded-none bg-hammerl-corpblue hover:bg-hammerl-ink text-white font-bold shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
            data-testid="login-submit-btn"
          >
            <LogIn className="w-4 h-4 mr-2" /> {loading ? "Synergisiere..." : "Karriere fortsetzen"}
          </Button>
          <div className="text-center text-sm">
            <span className="text-hammerl-pencil">Noch kein Reiter? </span>
            <Link to="/register" className="font-bold underline decoration-hammerl-corpblue decoration-2 underline-offset-2" data-testid="goto-register-link">
              Jetzt registrieren
            </Link>
          </div>
        </form>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <div className="bg-hammerl-sidebar border border-hammerl-ink p-3">
            <TrendingUp className="w-4 h-4 mb-1" />
            <div className="text-xs font-bold">10 Entscheidungen / Tag</div>
            <div className="text-[10px] font-mono text-hammerl-pencil">Cliffhanger garantiert.</div>
          </div>
          <div className="bg-hammerl-sidebar border border-hammerl-ink p-3">
            <Briefcase className="w-4 h-4 mb-1" />
            <div className="text-xs font-bold">DSGVO-konform</div>
            <div className="text-[10px] font-mono text-hammerl-pencil">Voigt zertifiziert.</div>
          </div>
        </div>
      </main>
    </div>
  );
}
